import { Grid } from '@atgfr8/atg-ui-tailwind';
import { apiClient } from '@api/apiClient';
import { currencyTypes } from '@common/commonLists';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { Combobox, BasicModal, DuplicateCarrierIndicator } from '@components';
import { openModal } from '@store/modals';
import {
  Carrier,
  CarrierFeatures,
  CarrierState,
  carrierStore,
  getCarrierDetail,
  getCarrierFeatures,
  load,
  orderStore,
  setCarrierStore,
  setLoadSureStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { userStore } from '@store/user';
import { CarrierAutoCompleteModel } from '@typeDefinitions/carrierTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import IsLTLLoad from '@views/order/components/models/IsLTLLoad';
import { IS_LTL_LOAD_MODAL_ID } from '@views/order/constants';
import { isSafetyRequired } from '@views/order/helpers';
import { produce } from 'solid-js/store';

import { StatusBadge } from '../StatusBadge';
import {
  cancelInsuranceCbModal,
  isLoadSureVendorAttached,
} from './LoadSureHelpers';
import {
  applyCoStoreManualCoveredBy,
  applyOwnedCarrierRep,
  isOwnedCapacityCoveredByEnabled,
  hasSavedCarrier,
} from './utils';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
  disabled?: boolean;
  size?: 'medium' | 'small' | undefined;
};

const setDefaultCurrencyType = (tabIndex: number) => {
  if (
    carrierStore.carrier.length &&
    !carrierStore.carrier[tabIndex]?.currencyOptionEnabled &&
    carrierStore.featureFlags.length > 0 &&
    !carrierStore.featureFlags[tabIndex].triumphPayCurrency
  ) {
    updateLoadPropertyAtIndex({
      currencyType: currencyTypes[0],
    });
  }
};

export const handleCarrierChange = async (
  carrierId: number,
  carrierName: string,
  tabIndex: number,
  load: ILoadEditViewModel,
) => {
  const ownedCapacityCoveredByEnabled = isOwnedCapacityCoveredByEnabled(
    load.mode,
  );
  if (!ownedCapacityCoveredByEnabled) {
    const ownedRepApplied = await applyOwnedCarrierRep(carrierId);

    if (!ownedRepApplied && userStore.user.officeId !== 63) {
      updateLoadPropertyAtIndex({
        coveredById: userStore.user.id,
        coveredBy: userStore.user.name,
      });
    }
  }

  await getCarrierFeatures(carrierId, tabIndex).then(() => {
    setDefaultCurrencyType(tabIndex);
  });

  const carrierDetail = await getCarrierDetail(carrierId, tabIndex);
  setDefaultCurrencyType(tabIndex);
  if (isSafetyRequired()) {
    openModal('safetyWarning');
  }
  if (ownedCapacityCoveredByEnabled && carrierDetail !== undefined) {
    applyCoStoreManualCoveredBy(carrierDetail, tabIndex);
  }

  updateLoadPropertyAtIndex({
    carrierName: carrierName,
    carrierId: carrierId,
    carrierContactId: carrierStore.carrier[tabIndex]?.contacts[0]?.id,
    status: load.status === 'Unassigned' ? 'Assigned' : load.status,
    carrierStatus: carrierStore.carrier[tabIndex]?.status,
    carrierActionPlan: carrierStore.carrier[tabIndex]?.carrierActionPlan,
    carrierMC: carrierStore.carrier[tabIndex]?.mc,
    quoteId: null,
    ltlquoteId: null,
    enableLoadCarrierRestrictionOverride: false,
    isCarrierDirty: true,
  });

  if (carrierStore.carrier[tabIndex]?.ltlEnabled && load.mode !== 'LTL') {
    openModal(IS_LTL_LOAD_MODAL_ID);
  }
};

export const clearCarrierValue = (tabIndex: number) => {
  const load = orderStore.order.loads[tabIndex];

  const clear = () => {
    setCarrierStore(
      produce((s: CarrierState) => {
        s.carrier[tabIndex] = {} as Carrier;
        s.featureFlags[tabIndex] = {} as CarrierFeatures;
      }),
    );
    // Clear driver in/out times when removing the carrier.
    const stops = load.stops?.map((stop) => ({
      ...stop,
      driverInTime: null,
      driverOutTime: null,
      //remove the conditional edi updates in this condition
      shouldSendEdiBooleanVals: {
        shouldSendPickupAppointmentAuto214: false,
        shouldSendDropoffAppointmentAuto214: false,
        shouldSendDropoffDriverInAuto214: false,
        shouldSendPickupDriverInAuto214: false,
        shouldSendPickupDriverOutAuto214: false,
        shouldSendDropoffDriverOutAuto214: false,
      },
      ediUpdates: [],
    }));
    updateLoadPropertyAtIndex({
      driver: undefined,
      driverPhoneNumber: undefined,
      truckNumber: undefined,
      trailerNumber: undefined,
      vinNumber: undefined,
      trackingEnabled: load.trackingServiceId ? load.trackingEnabled : false,
      carrierId: undefined,
      carrierName: undefined,
      carrierContactId: undefined,
      signedConfirmationDate: undefined,
      signedRateCon: false,
      status: 'Unassigned',
      stops: stops,
    });
  };

  if (isLoadSureVendorAttached()) {
    setLoadSureStore('callback', () => clear);
    openModal(cancelInsuranceCbModal);
  } else {
    clear();
  }
};

export const CarrierAutoComplete = (props: PropsT) => {
  const isClearDisabled = () =>
    ['Billed', 'Paid'].includes(props.load.status as string) ||
    (isFeatureFlagEnabled(
      FeatureFlags.EnableLoadDetailsCarrierBounceTracking,
    )() &&
      hasSavedCarrier(props.load));

  return (
    <>
      <Combobox<CarrierAutoCompleteModel>
        label="Carrier"
        disabled={props.disabled}
        error={
          ((load()?.forceCarrierChange || load()?.forceCustomerChange) ?? false)
            ? 'Carrier cannot be assigned to load'
            : null
        }
        value={carrierStore.carrier[props.tabIndex]?.name}
        fetchData={async (query: string) => {
          const response = await apiClient.get<
            string,
            { value?: CarrierAutoCompleteModel[] } | CarrierAutoCompleteModel[]
          >(
            `carrier/autocomplete?term=${encodeURIComponent(query.trimStart())}`,
          );
          const items =
            'value' in response && response.value
              ? response.value
              : (response as CarrierAutoCompleteModel[]);
          return items.slice(0, 50);
        }}
        getOptionLabel={(item: CarrierAutoCompleteModel) => item.name}
        disableClear={isClearDisabled()}
        onSelect={async (item: CarrierAutoCompleteModel | null) => {
          if (!item) {
            if (isClearDisabled()) {
              return;
            }
            clearCarrierValue(props.tabIndex);
            return;
          }

          if (isLoadSureVendorAttached()) {
            setLoadSureStore('callback', () => async () => {
              await handleCarrierChange(
                item.id,
                item.name,
                props.tabIndex,
                props.load,
              );
            });
            openModal(cancelInsuranceCbModal);
          } else {
            await handleCarrierChange(
              item.id,
              item.name,
              props.tabIndex,
              props.load,
            );
          }
        }}
        renderItem={(autoItem: CarrierAutoCompleteModel) => (
          <Grid container class="flex-nowrap justify-between">
            <Grid item>
              <Grid container direction="column">
                <Grid item class="font-bold">
                  {autoItem.name.length >= 60
                    ? autoItem.name.slice(0, 60) + '...'
                    : autoItem.name}
                </Grid>
                <Grid item class="text-black text-opacity-50">
                  MC: {autoItem.mc} | DOT: {autoItem.dot}
                </Grid>
                <Grid item class="text-black text-opacity-50">
                  Location: {autoItem.city}, {autoItem.state}
                </Grid>
              </Grid>
            </Grid>
            <Grid item class="pl-2">
              <Grid container direction="column">
                <Grid item class="self-center">
                  <Grid container direction={'row'} class="flex-nowrap">
                    <Grid item class="self-center pr-2">
                      Status
                    </Grid>
                    <Grid item class="self-center">
                      <StatusBadge
                        text={autoItem.status}
                        color={(text: string) => {
                          switch (text) {
                            case 'Active':
                              return '#28957C';
                            case 'Inactive':
                              return '#F59D25';
                            case 'Unvetted':
                              return '#78909c';
                            default:
                              return '#B00020';
                          }
                        }}
                        size="medium"
                      />
                    </Grid>
                  </Grid>
                  {autoItem.isDuplicate && (
                    <div class="pt-2 text-right">
                      <DuplicateCarrierIndicator id={autoItem.id} />
                    </div>
                  )}
                  <Grid item class="pt-2">
                    <Grid
                      container
                      direction={'row'}
                      class="flex-nowrap grow shrink-0 justify-end"
                    >
                      <Grid item class="self-center">
                        {autoItem.ltlEnabled && (
                          <StatusBadge
                            text="LTL"
                            color="#31637F"
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/*
                    NOTE: The carrier status chip has been commented out for now please dont delete it.
                    {autoItem.carrierStatus && (<div class="pt-2 text-right">
                      <Chip
                        id={`carrier-status-chip-${autoItem.id}`}
                        variant="filled"
                        backgroundColor={autoItem.carrierStatus === 'restricted' ? '#B00020' : '#F59D25'}
                        class="!rounded-sm !font-bold !text-white"
                        text={autoItem.carrierStatus === 'restricted' ? 'DNU' : 'Flagged'}
                      />
                    </div>
                  )} */}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      />
      <BasicModal
        id={IS_LTL_LOAD_MODAL_ID}
        header={false}
        footer={false}
        width="280px"
      >
        <IsLTLLoad modalId={IS_LTL_LOAD_MODAL_ID} />
      </BasicModal>
    </>
  );
};
